import { type Control, type FieldValues, type Path, useController } from 'react-hook-form'
import { StyleSheet, View, type ViewProps } from 'react-native'
import {
    HelperText,
    TextInput as PaperTextInput,
    type TextInputProps as PaperTextInputProps,
    useTheme,
} from 'react-native-paper'
import type { AppTheme } from '@/types/theme'

export type TextAreaInputProps<T extends FieldValues = Record<string, unknown>> = Omit<
    PaperTextInputProps,
    'name' | 'value' | 'onChangeText' | 'onBlur'
> & {
    name: Path<T>
    control: Control<T>
    rules?: Record<string, unknown>
    label?: string
    hint?: string
    wrapperProps?: ViewProps
    multiline?: boolean
    numberOfLines?: number
}

export function TextAreaInput<T extends FieldValues = Record<string, unknown>>(
    props: TextAreaInputProps<T>
) {
    const {
        label,
        hint,
        name,
        control,
        rules,
        wrapperProps = {},
        multiline = true,
        numberOfLines = 4,
        ...textAreaProps
    } = props

    const theme = useTheme<AppTheme>()
    const {
        field,
        fieldState: { error, invalid: _invalid, isTouched: _isTouched, isDirty: _isDirty },
        formState: { touchedFields: _touchedFields, dirtyFields: _dirtyFields },
    } = useController({
        name,
        control,
        rules,
    })

    return (
        <View
            style={[styles.inputWrapper, styles.textInputWrapper, wrapperProps.style]}
            {...wrapperProps}
        >
            <PaperTextInput
                {...textAreaProps}
                label={label}
                mode="outlined"
                multiline={multiline}
                numberOfLines={numberOfLines}
                value={field.value || ''}
                onChangeText={field.onChange}
                onBlur={field.onBlur}
                error={!!error}
                accessibilityLabel={label}
                testID={name}
                placeholderTextColor={theme.custom.placeholder}
            />
            {hint && !error ? <HelperText type="info">{hint}</HelperText> : null}
            {error ? <HelperText type="error">{error.message}</HelperText> : null}
        </View>
    )
}

const styles = StyleSheet.create({
    inputWrapper: {
        marginBottom: 8,
    },
    textInputWrapper: {},
})
